# Hirer identifiers

The SEEK API uses opaque [object identifier] strings to identify SEEK hirers.
Your software can programmatically convert between our numeric SEEK advertiser IDs and the new hirer IDs.
However, you’re encouraged to directly use hirer IDs whenever possible.

The [Developer Dashboard hirers page] can search for a hirer by either identifier.
You can then view all of their identifiers along with their [active relationships].

## Converting from a SEEK advertiser ID

The [`seekAnzAdvertiser` query] looks up a [`HiringOrganization`] from a SEEK advertiser ID.
You can select the resulting `HiringOrganization`’s hirer ID:

```graphql lovato-ignore-no-deprecated-schema
query ($legacyId: Int!) {
  seekAnzAdvertiser(id: $legacyId) {
    id {
      value
    }
  }
}
```

```json
{
  "legacyId": 10000
}
```

```json
{
  "seekAnzAdvertiser": {
    "id": {
      "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  }
}
```

The [`hiringOrganizations` query] will return a paginated list of all the SEEK hirers you have a [relationship] with.
You can select both the `id` & `seekAnzAdvertiserId` fields to produce a mapping between the identifiers:

```graphql lovato-ignore-no-deprecated-schema
query ($schemeId: String!, $first: Int) {
  hiringOrganizations(schemeId: $schemeId, first: $first) {
    edges {
      node {
        id {
          value
        }
        seekAnzAdvertiserId
        name
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "schemeId": "seekAnzPublicTest",
  "first": 5
}
```

```json
{
  "hiringOrganizations": {
    "edges": [
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
          },
          "seekAnzAdvertiserId": 10000,
          "name": "Acme Corp"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:A3HL8E4w"
          },
          "seekAnzAdvertiserId": 10002,
          "name": "Contoso"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:AYAWCibZ"
          },
          "seekAnzAdvertiserId": 10003,
          "name": "Fabrikam"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:FWzEydsm"
          },
          "seekAnzAdvertiserId": 10013,
          "name": "PixelArcade Studios"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:C2p2SCBR"
          },
          "seekAnzAdvertiserId": 10006,
          "name": "Prescott Pharmaceuticals"
        }
      }
    ],
    "pageInfo": {
      "hasNextPage": true,
      "endCursor": "seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR"
    }
  }
}
```

## Selecting an object’s SEEK advertiser ID

`HiringOrganization` objects have a [`seekAnzAdvertiserId` field].

For example, you can select the SEEK advertiser ID that posted a given [`PositionProfile`]:

```graphql lovato-ignore-no-deprecated-schema
query ($id: String!) {
  positionProfile(id: $id) {
    positionOrganizations {
      seekAnzAdvertiserId
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:287njieaj"
}
```

```json
{
  "positionProfile": {
    "positionOrganizations": [
      {
        "seekAnzAdvertiserId": 10001
      }
    ]
  }
}
```

[`seekanzadvertiserid` field]: /schema/named-type/HiringOrganization#/field/seekAnzAdvertiserId

## Converting to a SEEK advertiser ID

You can directly convert a hirer ID to a SEEK advertiser ID using the [`hiringOrganization` query].
This can be useful for exploratory testing or debugging:

```graphql lovato-ignore-no-deprecated-schema
query ($id: String!) {
  hiringOrganization(id: $id) {
    seekAnzAdvertiserId
    name
  }
}
```

```json
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "hiringOrganization": {
    "seekAnzAdvertiserId": 10000,
    "name": "Acme Corp"
  }
}
```

[`hiringorganization` query]: /schema/query/hiringOrganization
[`hiringorganization`]: /schema/named-type/HiringOrganization
[`hiringorganizations` query]: /schema/query/hiringOrganizations
[`positionprofile`]: /schema/named-type/PositionProfile
[`seekanzadvertiser` query]: /schema/query/seekAnzAdvertiser
[active relationships]: /auth/hirer-relationships
[Developer Dashboard hirers page]: /manage/hirers
[object identifier]: /graphql/seek-api-conventions#object-identifiers
[relationship]: /auth/hirer-relationships
